// OCR des tickets via l’API (clé LLM côté serveur uniquement)

import apiService from './apiService';

const VALID_CATEGORIES = new Set([
  'repas',
  'deplacement_vehicule',
  'peage',
  'hotel',
  'transport_public',
  'parking',
  'fournitures',
  'divers',
]);

/** Harmonise la catégorie renvoyée par le LLM avec les valeurs du formulaire PWA */
function normalizeCategorie(raw) {
  if (raw == null || raw === '') return 'divers';
  let s = String(raw).trim().toLowerCase();
  s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  s = s.replace(/\s+/g, '_').replace(/-/g, '_');
  const aliases = {
    deplacement: 'deplacement_vehicule',
    deplacement_vehicle: 'deplacement_vehicule',
    vehicule: 'deplacement_vehicule',
    carburant: 'deplacement_vehicule',
    essence: 'deplacement_vehicule',
    restaurant: 'repas',
    restauration: 'repas',
    cafe: 'repas',
    caf: 'repas',
    peage_autoroute: 'peage',
    sncf: 'transport_public',
    train: 'transport_public',
    metro: 'transport_public',
    bus: 'transport_public',
  };
  if (aliases[s]) s = aliases[s];
  if (VALID_CATEGORIES.has(s)) return s;
  return 'divers';
}

function coerceMontant(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const t = String(v).trim().replace(/\s/g, '').replace(',', '.');
  const n = parseFloat(t);
  return Number.isFinite(n) ? n : null;
}

class OCRService {
  /**
   * Extrait les données d'un ticket.
   * @param {File|Blob} fileOrBlob - Fichier image compressé (pas de base64 pour économiser la mémoire)
   */
  async extractTicketData(fileOrBlob) {
    const fallbackData = {
      montant: null,
      date: new Date().toISOString().split('T')[0],
      marchand: '',
      categorie: 'divers',
    };

    try {
      const file = fileOrBlob instanceof File
        ? fileOrBlob
        : new File([fileOrBlob], `ticket-${Date.now()}.jpg`, { type: fileOrBlob.type || 'image/jpeg' });
      const upload = await apiService.uploadNoteDeFraisTicket(file);
      if (!upload.success || !upload.file_url) {
        return {
          success: false,
          error: upload.error || 'Upload du justificatif échoué',
          data: fallbackData,
        };
      }

      const prompt = `Tu es un assistant comptable qui extrait les informations d'un ticket de caisse ou d'une facture.

Analyse cette image et extrais avec précision:
- montant : le montant TOTAL TTC (nombre décimal, ex: 12.50)
- tva : le montant de TVA si visible sur le ticket (nombre décimal, ex: 2.08). Si non visible, mettre null.
- taux_tva : le taux de TVA en % si visible (nombre, ex: 20). Si non visible, mettre null.
- date : la date de la dépense au format YYYY-MM-DD
- marchand : le nom du commerce/établissement
- categorie : la catégorie parmi: repas, deplacement_vehicule, peage, hotel, transport_public, parking, fournitures, divers

Réponds uniquement avec un objet JSON valide, sans texte autour.`;

      const llm = await apiService.invokeLlm({
        prompt,
        file_urls: [upload.file_url],
        response_json_schema: {
          type: 'object',
          properties: {
            montant: { type: 'number' },
            tva: { type: 'number' },
            taux_tva: { type: 'number' },
            date: { type: 'string' },
            marchand: { type: 'string' },
            categorie: { type: 'string' },
          },
        },
      });

      if (!llm.success) {
        return {
          success: false,
          error: llm.error || 'Erreur reconnaissance',
          data: fallbackData,
        };
      }

      const raw = llm.data;
      const extracted =
        raw != null && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};

      const montant = coerceMontant(
        extracted.montant ?? extracted.amount ?? extracted.total_ttc ?? extracted.total
      );

      // TVA : utiliser la valeur extraite ou calculer depuis le taux
      let tva = null;
      let taux_tva = extracted.taux_tva != null ? Number(extracted.taux_tva) : null;
      if (extracted.tva != null) {
        tva = Number(extracted.tva);
      } else if (taux_tva != null && taux_tva > 0 && montant != null) {
        tva = Math.round(montant * taux_tva / (100 + taux_tva) * 100) / 100;
      }

      return {
        success: true,
        file_url: upload.file_url, // URL du justificatif déjà uploadé (évite un double upload)
        data: {
          montant,
          tva,
          taux_tva,
          date: extracted.date || fallbackData.date,
          marchand: (extracted.marchand || extracted.merchant || extracted.etablissement || '').trim(),
          categorie: normalizeCategorie(extracted.categorie),
        },
      };
    } catch (error) {
      console.error('❌ Erreur OCR:', error);
      return {
        success: false,
        error: error.message,
        data: fallbackData,
      };
    }
  }
}

const ocrService = new OCRService();
export default ocrService;
